import React, { useMemo, useRef, useState } from "react";
import styles from "./index.module.css";
import CollapseIcon from "./CollapseIcon";
import ExpandIcon from "./ExpandIcon";
import PremierIcon from "./PremierIcon";
import DigitalIcon from "./DigitalIcon";
import TicketIcon from "./TicketIcon";
import Loader from "shared/Loader";
import Link from "shared/Link";

const formatPv = (value) => {
  if (value === null || value === undefined || value === "") return "—";

  return Number(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const getStatColorClass = (status) => {
  if (status === "green") return styles.goodValue;
  if (status === "red") return styles.badValue;
  return styles.mutedValue;
};

const getPvClassName = (row) => {
  return row.qualifiedPV ? styles.pvGood : styles.pvBad;
};

const StatusIcon = ({ active, Icon }) => {
  if (!active) return null;

  return (
    <div className={styles.statusIcon}>
      <Icon />
    </div>
  );
};

const getUniqueBy = (items, key) => {
  const seen = new Set();

  return items.filter((item) => {
    const value = item[key];
    if (seen.has(value)) return false;
    seen.add(value);
    return true;
  });
};

const QualificationRow = ({
  row,
  child = false,
  showPremierMembership,
  showDigitalDelivery,
  showFunctionTicket,
}) => {
  const isQualifiedIbo = row.qualifiedIbo;
  const isLevelZero = row.level === 0;

  return (
    <div className={`${styles.gridRow} ${child ? styles.childRow : ""}`}>
      <div className={styles.qualifiedCell}>
        {row.qualifiedLeg ? <div className={styles.qualifiedDot} /> : null}
      </div>

      <div className={styles.frontlineCell}>
        {isLevelZero ? (
          <div
            className={`${styles.namePill} ${
              isQualifiedIbo ? styles.namePillQualified : styles.namePillMuted
            }`}
          >
            {row.name}
          </div>
        ) : null}
      </div>

      <div
        className={`${styles.downlineCell} ${
          isLevelZero
            ? isQualifiedIbo
              ? styles.downlineQualified
              : styles.downlineMuted
            : ""
        }`}
      >
        {!isLevelZero ? (
          <Link
            to={`/membership/lookup/ibo/${row.iboNumber}`}
            useStyling={false}
            className={styles.noUnderlineLink}
            onClick={() => {}}
          >
            <span
              className={
                isQualifiedIbo ? styles.linkQualified : styles.linkText
              }
            >
              {row.name}
            </span>
          </Link>
        ) : null}
      </div>

      <div className={styles.levelCell}>
        {row.level && row.level !== 0 ? row.level : ""}
      </div>

      <div className={styles.iboCell}>
        {row.iboNumber ? (
          <Link
            to={`/membership/lookup/ibo/${row.iboNumber}`}
            useStyling={false}
            className={styles.noUnderlineLink}
            onClick={() => {}}
          >
            <span
              className={
                isQualifiedIbo ? styles.linkQualified : styles.linkText
              }
            >
              {row.iboNumber}
            </span>
          </Link>
        ) : null}
      </div>

      <div className={`${styles.pvCell} ${getPvClassName(row)}`}>
        {formatPv(row.pv)}
      </div>

      {showPremierMembership ? (
        <div className={styles.iconCell}>
          <StatusIcon active={row.premierMembership} Icon={PremierIcon} />
        </div>
      ) : null}

      {showDigitalDelivery ? (
        <div className={styles.iconCell}>
          <div className={styles.digitalIcon}>
            <StatusIcon active={row.digitalDelivery} Icon={DigitalIcon} />
          </div>
        </div>
      ) : null}

      {showFunctionTicket ? (
        <div className={`${styles.iconCell} ${styles.functionTicketCell}`}>
          <StatusIcon active={row.functionTicket} Icon={TicketIcon} />
        </div>
      ) : null}
    </div>
  );
};

const Qualifications = ({
  data,
  filters = [],
  selectedFilters,
  onRefresh,
  onViewQualificationDetails,
  onChangeFilter,
  filtersLoading,
  reportLoading,
  isRefreshing,
}) => {
  const [expanded, setExpanded] = useState(false);

  const reportRef = useRef(null);

  const onSaveAsPDF = () => {
    if (!reportRef.current) return;

    const pageStyles = Array.from(
      document.querySelectorAll('link[rel="stylesheet"], style')
    )
      .map((node) => node.outerHTML)
      .join("");

    const printHtml = `
    <html>
      <head>
        <title>${data?.title || "Qualifications Report"}</title>
        ${pageStyles}
        <style>
          @page {
            size: landscape;
            margin: 0.25in;
          }

          html,
          body {
            margin: 0 !important;
            padding: 0 !important;
            background: #fff !important;
            overflow: visible !important;
          }

          body * {
            visibility: visible !important;
          }

          .${styles.printReport} {
            position: static !important;
            left: auto !important;
            top: auto !important;
            width: 100% !important;
            max-width: none !important;
            min-width: 0 !important;
            height: auto !important;
            margin: 0 !important;
            padding: 0 !important;
            transform: none !important;
            overflow: visible !important;
            background: #fff !important;
          }

          .${styles.printHeader} {
            display: block !important;
            margin-bottom: 14px !important;
          }

          .${styles.printTitle} {
            font-size: 20px !important;
            font-weight: 700 !important;
            margin-bottom: 4px !important;
          }

          .${styles.printReport},
          .${styles.printReport} * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .${styles.printReport} a,
          .${styles.printReport} a:visited,
          .${styles.printReport} a:hover,
          .${styles.printReport} a:active {
            text-decoration: none !important;
            color: currentColor !important;
          }

          .${styles.printReport} .${styles.linkText} {
            color: #415364 !important;
            text-decoration: none !important;
          }

          .${styles.printReport} .${styles.linkQualified} {
            color: #43ba32 !important;
            text-decoration: none !important;
          }

          .${styles.qualifiedDot} {
            background: #55bc34 !important;
            border-radius: 50% !important;
          }

          .${styles.namePillQualified},
          .${styles.downlineQualified} {
            background: #55bc34 !important;
            color: #fff !important;
          }

          .${styles.namePillMuted},
          .${styles.downlineMuted} {
            background: #d7dde2 !important;
            color: #415364 !important;
          }

          .${styles.expandRow} {
            display: none !important;
          }

          .${styles.tableWrapper},
          .${styles.rows} {
            overflow: visible !important;
            height: auto !important;
            max-height: none !important;
          }

          .${styles.headerRow},
          .${styles.gridRow} {
            break-inside: avoid !important;
            page-break-inside: avoid !important;
          }
        </style>
      </head>
      <body>
        ${reportRef.current.outerHTML}

        <script>
          window.addEventListener("load", function () {
            setTimeout(function () {
              window.focus();
              window.print();
            }, 300);
          });

          window.addEventListener("afterprint", function () {
            window.close();
          });
        </script>
      </body>
    </html>
  `;

    const blob = new Blob([printHtml], { type: "text/html" });
    const blobUrl = URL.createObjectURL(blob);
    const printWindow = window.open(blobUrl, "_blank", "noopener,noreferrer");

    if (!printWindow) {
      URL.revokeObjectURL(blobUrl);
      return;
    }

    setTimeout(() => {
      URL.revokeObjectURL(blobUrl);
    }, 10000);
  };
  const stats = data?.stats || {};
  const columnVisibility = data?.columnVisibility || {
    showPremierMembership: false,
    showDigitalDelivery: false,
    showFunctionTicket: false,
  };
  const rows = data?.rows || [];
  const hasReportResponse = Boolean(data);
  const hasRows = rows.length > 0;

  const qualificationDetails = data?.qualificationDetails || {};
  const noteHtml = qualificationDetails.noteHtml || "";
  const hasNote = Boolean(noteHtml?.trim());

  const yearOptions = useMemo(() => {
    return getUniqueBy(filters, "year");
  }, [filters]);

  const monthOptions = useMemo(() => {
    if (!selectedFilters?.year) return [];

    return getUniqueBy(
      filters.filter(
        (item) => String(item.year) === String(selectedFilters.year)
      ),
      "monthString"
    );
  }, [filters, selectedFilters]);

  const eventOptions = useMemo(() => {
    if (!selectedFilters?.year || !selectedFilters?.monthString) return [];

    return getUniqueBy(
      filters.filter(
        (item) =>
          String(item.year) === String(selectedFilters.year) &&
          String(item.monthString) === String(selectedFilters.monthString)
      ),
      "eventType"
    );
  }, [filters, selectedFilters]);

  const reportOptions = useMemo(() => {
    if (
      !selectedFilters?.year ||
      !selectedFilters?.monthString ||
      !selectedFilters?.eventType
    ) {
      return [];
    }

    return getUniqueBy(
      filters.filter(
        (item) =>
          String(item.year) === String(selectedFilters.year) &&
          String(item.monthString) === String(selectedFilters.monthString) &&
          String(item.eventType) === String(selectedFilters.eventType)
      ),
      "report"
    );
  }, [filters, selectedFilters]);

  const statList = [stats.personalPv, stats.vcs, stats.qualifyingLegs].filter(
    Boolean
  );

  const visibleRows = useMemo(() => {
    const flattenedRows = rows.flatMap((row) => [
      { ...row, __child: false },
      ...((row.downline || []).map((child) => ({
        ...child,
        __child: true,
      })) || []),
    ]);

    if (expanded) {
      return flattenedRows;
    }

    return flattenedRows.filter((row) => !row.hideOnCollapse);
  }, [expanded, rows]);

  const columnTemplate = useMemo(() => {
    const columns = [
      "64px", // Qualified
      "180px", // Frontline
      "180px", // Downline
      "60px", // Level
      "110px", // IBO #
      "90px", // PV
    ];

    if (columnVisibility.showPremierMembership) {
      columns.push("minmax(70px, 130px)");
    }

    if (columnVisibility.showDigitalDelivery) {
      columns.push("minmax(70px, 130px)");
    }

    if (columnVisibility.showFunctionTicket) {
      columns.push("minmax(70px, 130px)");
    }

    return columns.join(" ");
  }, [columnVisibility]);

  return (
    <div className={styles.container}>
      <div className={styles.topBar}>
        {filtersLoading ? (
          <div className={styles.loader}>
            <Loader />
          </div>
        ) : (
          <div className={styles.filtersSection}>
            <div className={styles.filterField}>
              <label className={styles.filterLabel}>Qualifying Year</label>
              <select
                className={styles.select}
                value={selectedFilters?.year || ""}
                onChange={(event) =>
                  onChangeFilter?.("year", event.target.value)
                }
                disabled={filtersLoading}
              >
                <option value="">Select year</option>
                {yearOptions.map((item) => (
                  <option key={item.year} value={item.year}>
                    {item.year}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.filterField}>
              <label className={styles.filterLabel}>Qualifying Month</label>
              <select
                className={styles.select}
                value={selectedFilters?.monthString || ""}
                onChange={(event) =>
                  onChangeFilter?.("monthString", event.target.value)
                }
                disabled={!selectedFilters?.year || filtersLoading}
              >
                <option value="">Select month</option>
                {monthOptions.map((item) => (
                  <option
                    key={`${item.year}-${item.month}`}
                    value={item.monthString}
                  >
                    {item.monthString}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.filterField}>
              <label className={styles.filterLabel}>Event</label>
              <select
                className={`${styles.select} ${styles.eventSelect}`}
                value={selectedFilters?.eventType || ""}
                onChange={(event) =>
                  onChangeFilter?.("eventType", event.target.value)
                }
                disabled={!selectedFilters?.monthString || filtersLoading}
              >
                <option value="">Select event</option>
                {eventOptions.map((item) => (
                  <option
                    key={`${item.year}-${item.month}-${item.eventType}`}
                    value={item.eventType}
                  >
                    {item.eventType}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.filterField}>
              <label className={styles.filterLabel}>Qualification</label>
              <select
                className={styles.select}
                value={selectedFilters?.report || ""}
                onChange={(event) =>
                  onChangeFilter?.("report", event.target.value)
                }
                disabled={!selectedFilters?.eventType || filtersLoading}
              >
                <option value="">Select qualification</option>
                {reportOptions.map((item) => (
                  <option
                    key={`${item.year}-${item.month}-${item.eventType}-${item.report}`}
                    value={item.report}
                  >
                    {item.report}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {hasReportResponse && hasRows ? (
          <div className={styles.summarySection}>
            <button
              type="button"
              className={styles.detailsButton}
              onClick={onViewQualificationDetails}
            >
              Qualification Details
            </button>

            {!filtersLoading && !reportLoading ? (
              <button
                type="button"
                className={styles.detailsButton}
                onClick={onSaveAsPDF}
              >
                Save as PDF
              </button>
            ) : null}
          </div>
        ) : null}
        {hasReportResponse && !reportLoading && !filtersLoading ? (
          <div className={styles.refreshSection}>
            <button
              type="button"
              className={styles.refreshButton}
              onClick={onRefresh}
            >
              {isRefreshing ? <Loader size={20} color="#ffffff" /> : "Refresh"}
            </button>
          </div>
        ) : null}
      </div>

      {!filtersLoading && <div className={styles.topDivider} />}

      <div className={styles.statSection}>
        {statList.map((stat) => (
          <div className={styles.statBlock} key={stat.title}>
            <div className={styles.statLabel}>{stat.title}</div>
            <div className={styles.statValue}>
              <span className={getStatColorClass(stat.status)}>
                {stat.current}
              </span>
              <span className={styles.statDivider}>/</span>
              <span className={styles.mutedValue}>{stat.required}</span>
            </div>
          </div>
        ))}
      </div>

      {hasReportResponse && !reportLoading && !filtersLoading ? (
        <div className={styles.topDivider} />
      ) : null}

      <div ref={reportRef} className={styles.printReport}>
        {hasReportResponse ? (
          <div className={styles.printHeader}>
            <div className={styles.printTitle}>{data?.title}</div>
            <div>{data?.monthYear}</div>
            <div>{data?.reportIbo}</div>

            {statList.length ? (
              <div className={styles.printStatSection}>
                {statList.map((stat) => (
                  <div className={styles.printStatBlock} key={stat.title}>
                    <span className={styles.printStatLabel}>
                      {stat.title}:{" "}
                    </span>
                    <span className={getStatColorClass(stat.status)}>
                      {stat.current}
                    </span>
                    <span className={styles.statDivider}> / </span>
                    <span className={styles.mutedValue}>{stat.required}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {!filtersLoading && hasNote ? (
          <div
            className={styles.noteContent}
            dangerouslySetInnerHTML={{ __html: noteHtml }}
          />
        ) : null}

        {filtersLoading ? null : reportLoading ? (
          <div className={styles.loader}>
            <Loader />
          </div>
        ) : !hasReportResponse ? (
          <div className={styles.emptyState}>
            Select all filters to run the report.
          </div>
        ) : !hasRows ? (
          <div className={styles.emptyState}>
            There is no report data available
          </div>
        ) : (
          <>
            <div className={styles.expandRow}>
              <button
                type="button"
                className={styles.expandButton}
                onClick={() => setExpanded((value) => !value)}
              >
                <span className={styles.collapseIcon}>
                  {expanded ? <CollapseIcon /> : <ExpandIcon />}
                </span>
                {expanded ? "Collapse" : "Expand"}
              </button>
            </div>

            <div
              className={styles.tableWrapper}
              style={{ "--qualification-columns": columnTemplate }}
            >
              <div className={styles.headerRow}>
                <div>Qualified</div>
                <div>Frontline</div>
                <div>Downline</div>
                <div>Level</div>
                <div>IBO #</div>
                <div>PV</div>

                {columnVisibility.showPremierMembership ? (
                  <div className={styles.iconHeader}>Premier Membership</div>
                ) : null}

                {columnVisibility.showDigitalDelivery ? (
                  <div className={styles.iconHeader}>Digital Delivery</div>
                ) : null}

                {columnVisibility.showFunctionTicket ? (
                  <div className={styles.iconHeader}>Function Ticket</div>
                ) : null}
              </div>

              <div className={styles.rows}>
                {visibleRows.map((row) => (
                  <QualificationRow
                    key={`${row.id}-${row.__child ? "child" : "parent"}`}
                    row={row}
                    child={Boolean(row.__child)}
                    showPremierMembership={
                      columnVisibility.showPremierMembership
                    }
                    showDigitalDelivery={columnVisibility.showDigitalDelivery}
                    showFunctionTicket={columnVisibility.showFunctionTicket}
                  />
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Qualifications;
